import { api } from '../lib/api';

export interface OrderShipmentCandidate {
  productId: number;
  productSku?: string | null;
  productName?: string | null;
  parentProductId?: number | null;
  siteId: number;
  siteName: string | null;
  containerId: number | null;
  batchCode: string | null;
  uomItemId: number | null;
  uomLabel: string | null;
  containerCode: string | null;
  qtyOnHand: number;
}

export interface SalesOrderListItem {
  id: number;
  orderNumber: string;
  customerId: number | null;
  customerName: string | null;
  status: string;
  orderDate: string;
  requestedDeliveryDate: string | null;
  notes: string | null;
  totalQty: string | number;
  shippedQty: string | number;
  rowCount: number;
  shipmentCount: number;
}

export interface SalesOrderRow {
  id?: number;
  productId: number;
  description: string | null;
  qty: number;
  shippedQty?: number;
  draftQty?: number;
  uomItemId: number | null;
  unitPriceExVat?: number | null;
  vatRateItemId?: number | null;
  rowTotalExVat?: number | null;
  notes?: string | null;
  productSku?: string | null;
  productName?: string | null;
  productParentId?: number | null;
  uomLabel?: string | null;
  pricePreview?: {
    source: 'CUSTOMER_ROW' | 'DEFAULT_PRICE_LIST' | 'PRODUCT_DEFAULT' | 'NONE';
    priceListId: number | null;
    priceListName: string | null;
    customerId: number | null;
    customerName: string | null;
    unitPriceExVat: number | null;
    vatRateItemId: number | null;
    vatRateLabel: string | null;
  };
  allowedShipmentProducts?: Array<{
    id: number;
    parentProductId: number | null;
    sku: string | null;
    name: string;
    defaultUomItemId: number | null;
  }>;
  shipmentCandidates?: OrderShipmentCandidate[];
}

export interface SalesOrderDetail {
  id: number;
  orderNumber: string;
  customerId: number | null;
  customerName: string | null;
  status: string;
  orderDate: string;
  requestedDeliveryDate: string | null;
  customerSnapshotJson: Record<string, unknown> | null;
  notes: string | null;
  rows: SalesOrderRow[];
}

export interface ShipmentListItem {
  id: number;
  orderId: number;
  shipmentNumber: string | null;
  invoiceNumber: string | null;
  shipDate: string;
  siteId: number;
  siteName: string | null;
  notes: string | null;
  ledgerTxId: number | null;
  orderNumber: string;
  customerName: string | null;
  rowCount: number;
  totalQty: string | number;
}

export interface ShipmentRow {
  id: number;
  orderRowId: number | null;
  productId: number;
  batchCode: string | null;
  containerId: number | null;
  qtyShipped: number;
  uomItemId: number | null;
  notes: string | null;
  productSku: string | null;
  productName: string | null;
  uomLabel: string | null;
  containerCode: string | null;
}

export interface ShipmentDetail {
  id: number;
  orderId: number;
  shipmentNumber: string | null;
  invoiceNumber: string | null;
  shipDate: string;
  siteId: number;
  siteName: string | null;
  notes: string | null;
  ledgerTxId: number | null;
  orderNumber: string;
  customerName: string | null;
  rows: ShipmentRow[];
}

export interface OrdersMeta {
  customers: Array<{ id: number; name: string }>;
  products: Array<{
    id: number;
    sku: string | null;
    name: string;
    parentProductId: number | null;
    parentSku?: string | null;
    parentName?: string | null;
    defaultUomItemId: number | null;
    defaultVatRateItemId: number | null;
  }>;
  uoms: Array<{ id: number; label: string }>;
  sites: Array<{ id: number; name: string }>;
  statuses: string[];
  nextOrderNumber: string;
}

export interface SalesOrderFormData {
  orderNumber?: string;
  customerId: number | null;
  status: string;
  orderDate: string;
  requestedDeliveryDate?: string | null;
  notes?: string;
  rows: Array<{
    productId: number;
    description?: string | null;
    qty: number;
    uomItemId?: number | null;
    unitPriceExVat?: number | null;
    vatRateItemId?: number | null;
    rowTotalExVat?: number | null;
    notes?: string | null;
  }>;
}

export interface ShipmentCreatePayload {
  shipmentNumber?: string | null;
  invoiceNumber?: string | null;
  shipDate: string;
  siteId: number;
  notes?: string | null;
  rows: Array<{
    orderRowId: number;
    productId?: number | null;
    qtyShipped: number;
    batchCode?: string | null;
    containerId?: number | null;
    notes?: string | null;
  }>;
}

export interface ShipmentUpdatePayload extends ShipmentCreatePayload {}

export interface ShipmentFortnoxInvoiceResult {
  ok: boolean;
  requiresConfirmation?: boolean;
  warnings?: string[];
  invoiceNumber?: string;
  fortnoxInvoiceNumber?: string;
}

export interface OrderRowSnapshotPriceResult {
  ok: boolean;
  rowId: number;
  unitPriceExVat: number;
  vatRateItemId: number | null;
  rowTotalExVat: number;
  source: 'CUSTOMER_ROW' | 'DEFAULT_PRICE_LIST' | 'PRODUCT_DEFAULT' | 'NONE';
  priceListName: string | null;
}

export const ordersApi = {
  list: () => api.get<{ orders: SalesOrderListItem[] }>('/orders'),
  meta: () => api.get<OrdersMeta>('/orders/meta'),
  get: (id: number) => api.get<SalesOrderDetail>(`/orders/${id}`),
  create: (data: SalesOrderFormData) => api.post<{ id: number; orderNumber: string }>('/orders', data),
  update: (id: number, data: SalesOrderFormData) => api.patch<{ ok: boolean }>(`/orders/${id}`, data),
  applySnapshotPrice: (rowId: number) =>
    api.post<OrderRowSnapshotPriceResult>(`/orders/rows/${rowId}/snapshot-price`, { confirm: true }),
  ship: (id: number, data: ShipmentCreatePayload) => api.post<{ id: number; shipmentNumber: string }>(`/orders/${id}/ship`, data),
};

export const shipmentsApi = {
  list: (orderId?: number) =>
    api.get<{ shipments: ShipmentListItem[] }>(orderId ? `/shipments?orderId=${orderId}` : '/shipments'),
  get: (id: number) => api.get<ShipmentDetail>(`/shipments/${id}`),
  update: (id: number, data: ShipmentUpdatePayload) => api.patch<{ ok: boolean }>(`/shipments/${id}`, data),
  post: (id: number) => api.post<{ ok: boolean; shipmentNumber: string }>(`/shipments/${id}/post`, {}),
  createFortnoxInvoice: (id: number, force = false) =>
    api.post<ShipmentFortnoxInvoiceResult>(`/shipments/${id}/fortnox-invoice`, { force }),
};
